/**
 * Designations master — admin UI for the tenant's job-title catalogue.
 *
 * Path: /app/admin/designations  (company_admin / super_admin)
 *
 * Lets the company admin:
 *   - One-click seed a 36-title industry starter pack
 *   - Create / edit / delete custom designations
 *   - Group by function (Engineering / Sales / Marketing / etc.)
 *   - Optionally bind a default access role (system or custom RBAC) per title
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, PencilSimple, Trash, IdentificationBadge, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABELS = {
  company_admin: "Company Admin", country_head: "Country Head", region_head: "Region Head",
  branch_manager: "Branch Manager", sub_manager: "Sub Manager",
  assistant_manager: "Assistant Manager", employee: "Employee",
};

export default function Designations() {
  const [groups, setGroups] = useState({});
  const [systemRoles, setSystemRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | row

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/designations", { params: { active_only: false } }),
        api.get("/employees/roles/assignable"),
      ]);
      setGroups(d.data.groups || {});
      setSystemRoles(r.data.roles || []);
      setCustomRoles(r.data.custom_roles || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seedStarter = async () => {
    if (!window.confirm("This adds 36 industry-standard titles to your tenant. Existing titles are kept. Continue?")) return;
    try {
      const r = await api.post("/designations/seed");
      toast.success(`Inserted ${r.data.inserted}, skipped ${r.data.skipped} existing`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (did, title) => {
    if (!window.confirm(`Retire "${title}"? Existing employees keep this title; you just can't pick it on new ones.`)) return;
    try {
      await api.delete(`/designations/${did}`);
      toast.success("Retired");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalCount = Object.values(groups).reduce((acc, arr) => acc + arr.length, 0);

  return (
    <AppShell title="Designations">
      <div className="max-w-6xl mx-auto" data-testid="designations-page">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900">Designations</h1>
            <p className="text-sm text-zinc-600 mt-1">
              Your company&apos;s canonical job-title list. Replaces free-text typing across
              every employee, letter, and report — keeps your data clean.
            </p>
          </div>
          <div className="flex gap-2">
            {totalCount === 0 && (
              <Button variant="outline" onClick={seedStarter} data-testid="seed-btn">
                <Sparkle size={14} weight="duotone" className="mr-1" /> Use starter pack (36)
              </Button>
            )}
            <Button onClick={() => setEditing({})} data-testid="new-designation-btn">
              <Plus size={14} weight="bold" className="mr-1" /> New designation
            </Button>
          </div>
        </div>

        {loading ? (
          <Card className="p-6 text-sm text-zinc-500">Loading…</Card>
        ) : totalCount === 0 ? (
          <Card className="p-10 text-center" data-testid="empty-state">
            <IdentificationBadge size={42} weight="duotone" className="mx-auto text-zinc-400" />
            <div className="font-medium text-zinc-700 mt-3">No designations yet</div>
            <div className="text-sm text-zinc-500 mt-1">Start with our 36-title starter pack, or add your own.</div>
            <div className="mt-4 flex gap-2 justify-center">
              <Button onClick={seedStarter} data-testid="seed-btn-empty">
                <Sparkle size={14} weight="duotone" className="mr-1" /> Use starter pack
              </Button>
              <Button variant="outline" onClick={() => setEditing({})}>Add manually</Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-5">
            {Object.entries(groups).map(([fn, titles]) => (
              <Card key={fn} className="p-5" data-testid={`function-${fn.replace(/\s/g, "-")}`}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-zinc-900">{fn}</h2>
                  <Badge variant="outline">{titles.length} titles</Badge>
                </div>
                <div className="space-y-1">
                  {titles.map((t) => (
                    <div key={t.id} className={`flex items-center justify-between p-2.5 rounded ${!t.is_active ? "bg-zinc-50 opacity-60" : "hover:bg-zinc-50"}`} data-testid={`title-${t.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-medium text-zinc-900 truncate">{t.title}</span>
                        {t.band && <Badge variant="outline" className="text-[10px]">{t.band}</Badge>}
                        {t.default_role && <span className="text-[11px] text-zinc-500">defaults to: {ROLE_LABELS[t.default_role] || t.default_role}</span>}
                        {!t.is_active && <Badge variant="outline" className="text-[10px] bg-zinc-100">retired</Badge>}
                        {t.seeded && <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-200 bg-indigo-50">starter</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`edit-${t.id}`}><PencilSimple size={14} /></Button>
                        {t.is_active && <Button size="sm" variant="ghost" onClick={() => del(t.id, t.title)} data-testid={`delete-${t.id}`}><Trash size={14} className="text-red-600" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <Editor
          row={editing}
          systemRoles={systemRoles}
          customRoles={customRoles}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function Editor({ row, systemRoles, customRoles, onClose, onSaved }) {
  const isNew = !row.id;
  const [form, setForm] = useState({
    title: row.title || "",
    function: row.function || "",
    band: row.band || "",
    description: row.description || "",
    default_role: row.default_role || "",
    default_custom_role_id: row.default_custom_role_id || "",
    is_active: row.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (form.title.trim().length < 2) return toast.error("Title is too short");
    setSaving(true);
    const body = {
      title: form.title.trim(),
      function: form.function.trim() || null,
      band: form.band.trim() || null,
      description: form.description.trim() || null,
      default_role: form.default_role || null,
      default_custom_role_id: form.default_custom_role_id || null,
    };
    try {
      if (isNew) await api.post("/designations", body);
      else await api.patch(`/designations/${row.id}`, { ...body, is_active: form.is_active });
      toast.success(isNew ? "Designation created" : "Designation updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="designation-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "New designation" : `Edit — ${row.title}`}</DialogTitle>
          <DialogDescription>
            Designations become the canonical job-title list used across employee profiles,
            letters, and reports.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Senior Full-Stack Engineer" data-testid="form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Function</Label>
              <Input value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} placeholder="Engineering" list="function-suggestions" data-testid="form-function" />
              <datalist id="function-suggestions">
                <option value="Engineering" /><option value="Product" /><option value="Design" />
                <option value="Sales" /><option value="Marketing" /><option value="Operations" />
                <option value="Finance" /><option value="People & HR" /><option value="Legal & Compliance" />
                <option value="Customer Success" /><option value="Executive" />
              </datalist>
            </div>
            <div>
              <Label className="text-xs">Band / Grade</Label>
              <Input value={form.band} onChange={(e) => setForm({ ...form, band: e.target.value })} placeholder="B3" data-testid="form-band" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
          </div>
          <div>
            <Label className="text-xs">Default access role (optional)</Label>
            <Select value={form.default_role || "__none__"} onValueChange={(v) => setForm({ ...form, default_role: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="form-default-role"><SelectValue placeholder="No default — HR picks at creation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No default —</SelectItem>
                {systemRoles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-zinc-400 mt-1">
              When HR adds a new employee with this title, this base access role gets pre-selected.
            </div>
          </div>
          {customRoles.length > 0 && (
            <div>
              <Label className="text-xs">Default custom RBAC role (optional)</Label>
              <Select value={form.default_custom_role_id || "__none__"} onValueChange={(v) => setForm({ ...form, default_custom_role_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="form-default-custom-role"><SelectValue placeholder="No custom default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No default —</SelectItem>
                  {customRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} (inherits {ROLE_LABELS[r.inherits_from] || r.inherits_from})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-designation-btn">{saving ? "Saving…" : (isNew ? "Create" : "Save changes")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
